import type { Arena } from '../arena.ts';
import type { Element, Faction } from '../constants.ts';
import { type Dice, Rng } from '../dice.ts';
import type { LevelGeometry } from '../level.ts';
import { makeMatch, type MatchState } from '../match.ts';
import type { Vec3 } from '../math.ts';
import type { Barrier, Sign, Storm } from '../placed.ts';
import { eyeHeight, type PlayerState } from '../player.ts';
import type { SeriesState } from '../series.ts';

/**
 * The shapes the simulation is made of, and the constructors for them.
 *
 * Everything else under `sim/` imports from here and nothing here imports from
 * them, which is what keeps the split free of cycles. Types the rest of the
 * codebase uses are re-exported from `core/sim.ts`; import them from there
 * outside core.
 */

export interface RayHit {
  /** Where the ray met geometry, in world space. */
  point: Vec3;
  /** Distance from the ray origin, metres. */
  distance: number;
}

/**
 * The two things core needs from the outside world: can a straight line get from
 * here to there without hitting geometry, and where does a ray land. Rapier
 * answers both on the client, the same Rapier answers them on the server, and
 * core never imports either.
 *
 * `raycast` exists for placed effects — it puts a storm on the floor the player
 * is actually looking at, and grounds a wall. It replaces a `groundAt` that sat
 * on this interface unimplemented, because a vertical probe cannot tell you
 * where someone is aiming.
 */
export interface WorldQuery {
  /** True when the segment is blocked by level geometry. */
  segmentBlocked(from: Vec3, to: Vec3): boolean;
  /** First geometry hit along `dir`, which must be normalised. */
  raycast(from: Vec3, dir: Vec3, maxDistance: number): RayHit | null;

  /**
   * Puts a body exactly where the simulation says, after a teleport.
   *
   * Optional because a headless world has no bodies to move. Needed between
   * rounds: everybody is put back on a spawn at once, and a capsule left at last
   * round's position would resolve its first move from there and drag the mage
   * back across the map.
   */
  place?(p: PlayerState): void;

  /**
   * Swaps the level's colliders for another map's, between rounds.
   *
   * Optional because a headless `WorldQuery` — the stub a core test hands in —
   * has no colliders to swap. Everything that does have them must implement it,
   * or a rotation leaves the simulation resolving movement and line of sight
   * against the map everybody has just left.
   */
  rebuildLevel?(geometry: LevelGeometry): void;
  /**
   * Tells the world which spell walls stand right now, so the solid ones can
   * be given collision. Called by the room after every tick.
   *
   * Core decides everything about a barrier — who it stops, who it slows, when
   * it ends — but walking into one is the world's question. Optional for the
   * same reason the rest of this section is: a test world with no bodies has
   * nothing to collide.
   */
  syncBarriers?(barriers: readonly Barrier[]): void;
  /**
   * Tells the world who is in the match and where, so every mage has a capsule
   * to bump into. Called by the room before every tick. Bodies for players no
   * longer listed are removed.
   */
  syncBodies?(players: readonly PlayerState[]): void;

  /**
   * Slides a mage's body by `delta` and reports where it ended up.
   *
   * The seam the whole architecture turns on, and what let the renderer stop
   * owning the local player.
   *
   * Every *rule* about movement — how fast, whether they may jump, what gravity
   * does, how an impulse decays, how fast flight climbs — belongs to the
   * simulation and lives in `movePlayer`. What does not, and cannot, is
   * resolving a capsule against level geometry: that wants a real physics engine
   * with autostep and slopes and ground snapping, and the answer differs by
   * where the code is running. So core decides *where a mage is trying to go*
   * and asks the world to say where they actually got.
   *
   * Optional, and may return null for a mage it has no body for. Bots, tests and
   * a headless server without physics all fall back to a cruder resolution that
   * slides along walls and rides a downward ray to the floor — good enough for
   * something following a navigation grid that already keeps it off the walls,
   * and not good enough for a person, which is exactly the distinction.
   */
  moveBody?(p: PlayerState, delta: Vec3, dt: number): BodyMove | null;
}

/** Where a body got to, and whether it is standing on something. */
export interface BodyMove {
  pos: Vec3;
  grounded: boolean;
}

export interface PlayerInput {
  /** -1..1 */
  moveForward: number;
  moveRight: number;
  yaw: number;
  pitch: number;
  jump: boolean;
  run: boolean;
  fire: boolean;
  flyUp: boolean;
  flyDown: boolean;
  /** 0..5, or -1 to leave the selection alone. */
  selectSlot: number;
  /** Held, not toggled. Ignored while flying, where the same key descends. */
  crouch: boolean;
  /** Cleric innate: pull the nearest fallen ally to your feet. */
  callGhost: boolean;
  /** Cleric innate: bring a ghost at your feet back to life. */
  resurrect: boolean;
  selfResurrect: boolean;
  /**
   * Monotonic count of Tab presses, for tuning a protection to an element. A
   * count rather than a flag so that a press landing between two ticks is not
   * simply lost.
   */
  elementCycles: number;
}

export function emptyInput(): PlayerInput {
  return {
    moveForward: 0, moveRight: 0, yaw: 0, pitch: 0,
    jump: false, run: false, fire: false, flyUp: false, flyDown: false,
    selectSlot: -1, crouch: false, callGhost: false, resurrect: false,
    selfResurrect: false,
    elementCycles: 0,
  };
}

export interface Projectile {
  id: number;
  ownerId: string;
  faction: Faction;
  spellId: string;
  element: Element;
  dice: Dice;
  pos: Vec3;
  dir: Vec3;
  speed: number;
  travelled: number;
  maxRange: number;
  /** Blast radius, zero for single-target. */
  radius: number;
  /**
   * A status this projectile carries instead of damage. Debuffs travel like any
   * other spell now, so they use the same swept capsule test and are stopped by
   * the same walls.
   */
  applies?: 'debuff';
  /** How long the carried status lasts. */
  duration?: number;
  /** The status rolls its dice every second rather than once on landing. */
  ticks?: boolean;
  /** 1..5. Only the look uses this; the numbers were resolved at cast. */
  tier: number;
  /** Ticks of burning applied on hit, resolved at cast for the caster's tier. */
  burnTicks?: number;
  /** Speed left, and for how long. Ice's rider. See `applyChill`. */
  chill?: number;
  chillSeconds?: number;
  /** How many further enemies a blast jumps to. See `applyChain`. */
  chainTargets?: number;
  /** How hard this shoves what it hits, metres per second. */
  knockback?: number;
  /** Power torn out of whatever it hits. */
  ppDrain?: number;
}

export type SimEvent =
  | { type: 'cast'; playerId: string; spellId: string; tier: number }
  | { type: 'cast-start'; playerId: string; spellId: string; seconds: number }
  | { type: 'cast-cancel'; playerId: string; spellId: string }
  /**
   * A bolt, drawn from the caster's eye to wherever it stopped.
   *
   * Carries its endpoint because a hitscan has nothing for the renderer to
   * follow — the beam is the entire visual, and it exists for one frame's worth
   * of simulation whether it hit somebody, hit a wall, or reached its limit.
   */
  | {
    type: 'beam'; playerId: string; spellId: string; element: Element;
    from: Vec3; to: Vec3;
  }
  /**
   * A cast that could not take effect, and why.
   *
   * The silence was the problem. A spell that reaches nobody costs no power and
   * starts no cooldown, which is right — but it is also completely quiet, so
   * from the caster's side it is indistinguishable from a key that does not
   * work. That is exactly how Pain presented while its range was broken: the
   * feedback was correct, and there was none of it.
   *
   * `reason` is a plain phrase rather than a code because the only consumer is
   * a line of text on the HUD, and one fewer table to keep in step is worth more
   * than the indirection.
   */
  | { type: 'fizzle'; playerId: string; spellId: string; reason: string }
  | {
    type: 'hit'; targetId: string; sourceId: string; amount: number; element: Element;
    /**
     * Whether this is a tick of something already burning rather than a blow.
     *
     * The difference matters to the reader more than to the target. Damage
     * numbers accumulate drizzle into one climbing figure and give every *blow*
     * its own, which is only possible if the two can be told apart — and until
     * now they could not, because over-time damage emitted no event at all.
     */
    overTime?: boolean;
  }
  | { type: 'arc'; sourceId: string; targetId: string; element: Element }
  | { type: 'heal'; targetId: string; sourceId: string; amount: number }
  | { type: 'headshot'; sourceId: string; targetId: string }
  | { type: 'debuff'; sourceId: string; targetId: string; spellId: string }
  /**
   * A spell finishing its journey, wherever and however.
   *
   * Nothing reported this before: `hit` only fires when a player is struck and
   * carries no position, so a spell burying itself in a wall produced no event
   * at all and there was nothing for an impact effect — or, later, an impact
   * sound — to hang on.
   */
  | {
      type: 'impact';
      spellId: string;
      element: Element;
      pos: Vec3;
      /** Direction of travel, so an effect can face back down the path. */
      dir: Vec3;
      /** What it ran into. `expired` is a spell that simply ran out of range. */
      against: 'player' | 'barrier' | 'level' | 'expired';
      /** Blast radius, for the effects that need to match a real explosion. */
      radius: number;
      tier: number;
    }
  | { type: 'death'; playerId: string; killerId: string | null }
  | { type: 'raised'; playerId: string; byId: string }
  | { type: 'called'; playerId: string; byId: string }
  | { type: 'valhalla'; playerId: string }
  | { type: 'respawn'; playerId: string }
  | { type: 'level'; playerId: string; level: number }
  | { type: 'pool'; poolId: string; faction: Faction }
  | { type: 'shrine'; shrineId: string; faction: Faction; conquered: boolean }
  | { type: 'eliminated'; faction: Faction }
  | { type: 'victory'; faction: Faction | null; reason: string }
  /**
   * A round of a set has been decided, and the scoreboard is going up.
   *
   * Carries the whole set score rather than just the winner, so a client that
   * missed a round — joined late, dropped and came back — draws the right
   * numbers from the first one of these it sees instead of counting along from
   * whenever it happened to be listening.
   */
  | {
    type: 'round-over';
    winner: Faction | null;
    wins: Record<Faction, number>;
    played: number;
    bestOf: number;
    /** Set once somebody has taken the set. The scoreboard after this is final. */
    seriesWinner: Faction | null;
    /** Where the next round is, or null when there is not one. */
    nextMap: string | null;
  }
  /**
   * The lobby is over and the match is beginning.
   *
   * Sent to everybody at once, which is the point: a lobby countdown each client
   * ran for itself meant one person hitting ready started a match nobody else
   * was in. One room, one clock, one moment.
   */
  | { type: 'match-start' }
  | {
    type: 'round-start';
    round: number;
    bestOf: number;
    mapId: string;
    wins: Record<Faction, number>;
  };

/**
 * A player as the scoreboard knows them: who, not where.
 *
 * Deliberately carries no position, heading or health. Those are the things
 * relevancy exists to withhold, and a roster that leaked them would be a filter
 * with a hole in it rather than a scoreboard.
 */
export interface RosterEntry {
  id: string;
  name: string;
  faction: Faction;
  cls: string;
  level: number;
  alive: boolean;
  ghost: boolean;
  /** Whether they have said they are ready to start. Bots always are. */
  ready: boolean;
  /**
   * Walking the corridor home.
   *
   * Alive and not a ghost, but not somebody you can meet — so a headcount of who
   * is in the fight has to know, and it is no more a secret than being dead is.
   */
  inValhalla: boolean;
}

/** The roster for a state, which for an unfiltered one is simply everybody. */
export function rosterOf(players: readonly PlayerState[]): RosterEntry[] {
  return players.map((p) => ({
    id: p.id,
    name: p.name,
    faction: p.faction,
    cls: p.cls,
    level: p.level,
    alive: p.alive,
    ghost: p.ghost,
    ready: p.ready ?? false,
    inValhalla: p.inValhalla,
  }));
}

export interface SimState {
  arena: Arena;
  players: PlayerState[];
  projectiles: Projectile[];
  /** Placed effects. Core owns these; the renderer only mirrors them. */
  storms: Storm[];
  barriers: Barrier[];
  signs: Sign[];
  match: MatchState;
  /**
   * The set this round belongs to, when the room is playing one.
   *
   * On the state so it reaches every client in the ordinary snapshot: the round
   * score is the thing players look at between rounds, and a scoreboard fetched
   * separately would be a second source of truth that could disagree with the
   * one the match is actually being decided by.
   */
  series: SeriesState | null;
  /** Seconds left of the scoreboard, while the phase is `intermission`. */
  intermission: number;
  /** Seconds left of the pre-round freeze, while the phase is `warmup`. */
  warmup: number;
  /**
   * Seconds left in the lobby, while the phase is `lobby`.
   *
   * Shortened when everybody says they are ready, which is `countdown` in
   * `core/lobby.ts` — a rule that existed long before anything ran it.
   */
  lobbyLeft: number;
  /**
   * Everybody in the match, whether or not you can see them.
   *
   * `players` is filtered by relevancy — an enemy you have never laid eyes on is
   * not in it, which is the whole point and is what stops a client knowing where
   * the other side is. But the *scoreboard* is not a secret: every shooter shows
   * you the enemy roster from the moment you spawn, and hiding it meant the
   * enemy list filled in as people wandered into view and emptied again when
   * they left, and a kill between two players you had not seen read as
   * "somebody killed somebody".
   *
   * So identity travels separately from position. Names, sides, classes, levels
   * and whether somebody is standing — none of which tells you where they are.
   */
  roster: RosterEntry[];
  /**
   * Multiplier on all experience earned, from the lobby.
   *
   * On the state rather than passed to each award because it has to survive a
   * snapshot: a client predicting a kill has to reach the same level at the same
   * moment the server does, and a rate the client did not know about would make
   * every level-up a correction.
   */
  xpScale: number;
  rng: Rng;
  tick: number;
  nextProjectileId: number;
  nextPlacedId: number;
}

/**
 * Build a simulation. Everything that constructs a `SimState` goes through here
 * rather than writing the literal out, so that adding a field is one edit and
 * not one edit per test.
 */
export function makeSimState(
  arena: Arena, players: PlayerState[], seed = 0xc0ffee,
  rules: {
    roundSeconds?: number; xpScale?: number; series?: SeriesState | null;
    /**
     * The pre-round freeze. Defaults to none: a bare state plays at once, and a
     * room passes `WARMUP_SECONDS` because a round is a thing you get ready for.
     */
    warmupSeconds?: number;
    /**
     * How long the room waits in the lobby before starting itself. Zero means
     * there is no lobby at all, which is what a bare state and every core test
     * wants.
     */
    lobbySeconds?: number;
  } = {},
): SimState {
  return {
    arena,
    players,
    projectiles: [],
    storms: [],
    barriers: [],
    signs: [],
    /*
     * The lobby's rules, carried on the state rather than read from constants.
     *
     * They were declared on `RoomSettings` and used by nothing: a host could set
     * a twenty-five minute round and a double experience rate, the screen would
     * show both, the summary would report both, and the match would run for the
     * default fifteen at the default rate. The settings were real everywhere
     * except where they mattered.
     */
    match: makeMatch(rules.roundSeconds),
    series: rules.series ?? null,
    intermission: 0,
    roster: [],
    /*
     * No freeze unless somebody asks for one.
     *
     * `WARMUP_SECONDS` by default turned every headless test that steps a state
     * into ten simulated seconds of nothing happening, and the ones that loop
     * until something does never got there. The freeze belongs to a *room* —
     * rounds do, and this is the moment before one — so the room asks for it and
     * a bare state starts playing immediately, which is what every caller of
     * `makeSimState` has always assumed.
     */
    warmup: rules.warmupSeconds ?? 0,
    lobbyLeft: rules.lobbySeconds ?? 0,
    xpScale: rules.xpScale && rules.xpScale > 0 ? rules.xpScale : 1,
    rng: new Rng(seed),
    tick: 0,
    nextProjectileId: 1,
    nextPlacedId: 1,
  };
}

export function findPlayer(s: SimState, id: string): PlayerState | undefined {
  return s.players.find((p) => p.id === id);
}
export const PLAYER_RADIUS = 0.55;

export function eyePos(p: PlayerState): Vec3 {
  return { x: p.pos.x, y: p.pos.y + eyeHeight(p), z: p.pos.z };
}
